/**
 * AdminEnvCheckPage — Batch pré-Republish 2026-09-04.
 *
 * Compare `os.environ` (runtime effectif Prod, Dashboard-first) vs
 * `backend/.env` (fichier committé). Détecte les écrasements silencieux
 * du Dashboard Secrets Emergent (rejeu du bug 28/08 sur les vars JWKS).
 *
 * Contrat sécurité :
 *   - Aucune valeur secrète en clair côté frontend. Les entrées `is_secret=true`
 *     du payload backend ne contiennent QUE `last4` + `sha256_8`.
 *   - Route protégée `adminOnly` côté frontend (miroir du `require_admin` backend).
 *
 * Ergonomie iPad : bouton h-14, table `overflow-x-auto`, ligne surlignée
 * rouge sur divergence, tri auto (divergences en tête, secrets ensuite,
 * puis publics).
 */
import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  ShieldAlert,
  AlertTriangle,
  Info,
  Fingerprint,
  Globe2,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Clés avec avertissement produit spécifique (tooltip contextuel dans le tableau).
const KEY_WARNINGS = {
  GOOGLE_TOKEN_ENCRYPTION_KEY:
    "Si divergence détectée : les connexions Google Calendar existantes deviennent illisibles et doivent être refaites par chaque utilisateur.",
  JWT_SECRET:
    "Si divergence détectée : tous les JWT en cours sont invalidés — tous les users seront déconnectés au prochain call API.",
  MONGO_URL:
    "En Prod, cette clé peut légitimement diverger (Dashboard-only). Vérifier que le runtime pointe bien vers le cluster Prod, pas Preview.",
  DB_NAME:
    "En Prod, cette clé peut légitimement diverger (Dashboard-only). Vérifier que le runtime pointe bien vers la DB Prod, pas `test_database`.",
};

// Priorité de tri (bas de la fonction) : divergences > missing > matches.
function sortRows(vars) {
  const priority = (v) => {
    if (!v.match) {
      if (v.note?.startsWith("DIVERGES")) return 0;
      if (v.note?.startsWith("MISSING in runtime")) return 1;
      if (v.note?.startsWith("MISSING in .env")) return 2;
      return 3; // both missing
    }
    return 10;
  };
  return [...vars].sort((a, b) => {
    const p = priority(a) - priority(b);
    if (p !== 0) return p;
    // Secondary : secrets en premier, puis publics
    if (a.is_secret !== b.is_secret) return a.is_secret ? -1 : 1;
    return a.key.localeCompare(b.key);
  });
}

function StatusIcon({ match }) {
  if (match) {
    return (
      <CheckCircle2
        className="h-5 w-5 text-emerald-400"
        data-testid="env-check-status-ok"
      />
    );
  }
  return (
    <XCircle
      className="h-5 w-5 text-red-400"
      data-testid="env-check-status-ko"
    />
  );
}

function ValueCell({ entry, side }) {
  // side = "runtime" | "dotenv"
  if (!entry.is_secret) {
    const val = side === "runtime" ? entry.runtime_value : entry.dotenv_value;
    if (!val) {
      return <span className="text-slate-500 italic">(vide)</span>;
    }
    return (
      <span className="font-mono text-xs break-all text-slate-200">{val}</span>
    );
  }
  const last4 = side === "runtime" ? entry.runtime_last4 : entry.dotenv_last4;
  const hash = side === "runtime" ? entry.runtime_sha256_8 : entry.dotenv_sha256_8;
  if (!last4 && !hash) {
    return <span className="text-slate-500 italic">(vide/absent)</span>;
  }
  return (
    <span className="font-mono text-xs text-slate-200 whitespace-nowrap">
      …{last4}
      <span className="ml-2 text-slate-500">sha:{hash}</span>
    </span>
  );
}

function NoteCell({ entry }) {
  const note = entry.note || "";
  const cls = entry.match
    ? "badge-success"
    : note.startsWith("DIVERGES")
      ? "badge-error"
      : note.startsWith("MISSING in .env")
        ? "badge-neutral"
        : "badge-warning";
  const label = entry.match
    ? "OK"
    : note.startsWith("DIVERGES")
      ? "DIVERGE"
      : note.startsWith("MISSING in runtime")
        ? "Manque en runtime"
        : note.startsWith("MISSING in .env")
          ? "Runtime-only (Dashboard)"
          : "Absente";
  return (
    <div className="flex flex-col gap-1">
      <Badge variant="outline" className={cls}>{label}</Badge>
      {!entry.match && (
        <span className="text-[11px] leading-tight text-slate-500">
          {note}
        </span>
      )}
    </div>
  );
}

export default function AdminEnvCheckPage() {
  const [state, setState] = useState("loading"); // loading | ready | error
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (opts = {}) => {
    if (opts.refresh) setRefreshing(true);
    else setState("loading");
    setError("");
    try {
      const { data } = await api.get("/admin/env-check");
      setPayload(data);
      setState("ready");
    } catch (e) {
      const status = e?.response?.status;
      let msg;
      if (status === 401) msg = "Session expirée — reconnectez-vous.";
      else if (status === 403) msg = "Accès admin requis.";
      else if (status >= 500) msg = "Erreur serveur — réessayez ou consultez les logs.";
      else msg = formatApiError(e);
      setError(msg);
      setState("error");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = payload?.summary;
  const hasDiverges = summary && summary.diverges > 0;
  const hasMissing =
    summary &&
    (summary.missing_runtime > 0 || summary.missing_file > 0 || summary.both_missing > 0);
  const rows = payload ? sortRows(payload.vars) : [];

  return (
    <TooltipProvider>
      <div
        className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6"
        data-testid="admin-env-check-page"
      >
        {/* En-tête */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
            <ShieldAlert className="h-4 w-4" />
            Administration
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1
                className="text-2xl font-semibold text-slate-100 sm:text-3xl"
                data-testid="admin-env-check-heading"
              >
                Vérification des variables d&apos;environnement
              </h1>
              <p className="mt-1 max-w-3xl text-sm text-slate-400">
                Compare le <strong className="text-slate-200">runtime</strong> (valeurs
                effectives lues par le serveur, Dashboard Emergent prioritaire) au
                fichier <code className="rounded bg-slate-800 px-1.5 py-0.5 text-xs">backend/.env</code>{" "}
                committé. Divergence = le Dashboard a écrasé le fichier. Peut être
                normal (Prod-only : DB_NAME, MONGO_URL, TIGRIS_*) ou anormal (bug
                28/08 : JWKS écrasés par valeurs périmées).
              </p>
            </div>
            <Button
              onClick={() => load({ refresh: true })}
              disabled={refreshing || state === "loading"}
              className="h-14 min-w-[180px] shrink-0 gap-2 bg-[#C4553B] text-base font-semibold text-white hover:bg-[#A8402C] disabled:opacity-60"
              data-testid="admin-env-check-refresh-btn"
            >
              {refreshing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" /> Analyse…
                </>
              ) : (
                <>
                  <RefreshCw className="h-5 w-5" /> Rafraîchir
                </>
              )}
            </Button>
          </div>
        </div>

        {/* État loading initial */}
        {state === "loading" && (
          <Card className="border-slate-800 bg-slate-900/40">
            <CardContent
              className="flex items-center justify-center gap-3 p-8 text-slate-400"
              data-testid="admin-env-check-loading"
            >
              <Loader2 className="h-5 w-5 animate-spin" />
              Chargement des variables…
            </CardContent>
          </Card>
        )}

        {/* État error */}
        {state === "error" && (
          <Card className="border-red-800 bg-red-950/30">
            <CardContent className="p-4" data-testid="admin-env-check-error">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-red-400" />
                <div>
                  <div className="font-medium text-red-300">Erreur</div>
                  <div className="mt-1 text-sm text-red-400/80">{error}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* État success */}
        {state === "ready" && payload && (
          <>
            {/* Résumé */}
            <Card
              className={`border ${
                hasDiverges
                  ? "border-red-800/60 bg-red-950/20"
                  : hasMissing
                    ? "border-amber-800/40 bg-amber-950/10"
                    : "border-emerald-800/40 bg-emerald-950/10"
              }`}
              data-testid="admin-env-check-summary"
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    {hasDiverges ? (
                      <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-red-400" />
                    ) : hasMissing ? (
                      <Info className="mt-0.5 h-6 w-6 shrink-0 text-amber-400" />
                    ) : (
                      <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-400" />
                    )}
                    <div>
                      <div
                        className={`font-semibold ${
                          hasDiverges
                            ? "text-red-200"
                            : hasMissing
                              ? "text-amber-200"
                              : "text-emerald-200"
                        }`}
                        data-testid="admin-env-check-summary-title"
                      >
                        {hasDiverges
                          ? "⚠️ Divergences détectées"
                          : hasMissing
                            ? "Variables absentes détectées"
                            : "✅ Toutes les variables correspondent"}
                      </div>
                      <p className="mt-1 text-sm text-slate-400">
                        Dernière vérification&nbsp;:{" "}
                        <span className="font-mono text-xs">
                          {new Date(payload.checked_at).toLocaleString("fr-BE")}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge
                      variant="outline"
                      className="badge-neutral"
                      data-testid="admin-env-check-total"
                    >
                      {summary.total_checked} clés vérifiées
                    </Badge>
                    <Badge
                      variant="outline"
                      className="badge-success"
                      data-testid="admin-env-check-matches"
                    >
                      {summary.matches} correspondent
                    </Badge>
                    {summary.diverges > 0 && (
                      <Badge
                        variant="outline"
                        className="badge-error"
                        data-testid="admin-env-check-diverges"
                      >
                        {summary.diverges} divergent
                      </Badge>
                    )}
                    {summary.missing_runtime + summary.missing_file + summary.both_missing > 0 && (
                      <Badge
                        variant="outline"
                        className="badge-warning"
                        data-testid="admin-env-check-missing"
                      >
                        {summary.missing_runtime + summary.missing_file + summary.both_missing} vides
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tableau des clés */}
            <Card
              className="border-slate-800 bg-slate-900/40"
              data-testid="admin-env-check-table-card"
            >
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="p-3 text-left font-medium">Clé</th>
                        <th className="p-3 text-left font-medium">Type</th>
                        <th className="p-3 text-center font-medium">Statut</th>
                        <th className="p-3 text-left font-medium">Runtime</th>
                        <th className="p-3 text-left font-medium">.env</th>
                        <th className="p-3 text-left font-medium">Note</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {rows.map((entry) => {
                        const warning = KEY_WARNINGS[entry.key];
                        const rowCls = !entry.match
                          ? entry.note?.startsWith("DIVERGES")
                            ? "bg-red-950/30 hover:bg-red-950/40"
                            : "bg-amber-950/10 hover:bg-amber-950/20"
                          : "hover:bg-slate-800/40";
                        return (
                          <tr
                            key={entry.key}
                            className={rowCls}
                            data-testid={`admin-env-check-row-${entry.key}`}
                          >
                            <td className="p-3 align-top">
                              <div className="flex items-center gap-1.5">
                                <code
                                  className="font-mono text-xs text-slate-100"
                                  data-testid={`admin-env-check-key-${entry.key}`}
                                >
                                  {entry.key}
                                </code>
                                {warning && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        className="text-amber-400 hover:text-amber-300"
                                        data-testid={`admin-env-check-warning-${entry.key}`}
                                        aria-label={`Avertissement produit pour ${entry.key}`}
                                      >
                                        <Info className="h-4 w-4" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="right"
                                      className="max-w-xs bg-slate-950 text-slate-100 border border-slate-700"
                                    >
                                      <p className="text-xs leading-relaxed">{warning}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            </td>
                            <td className="p-3 align-top">
                              {entry.is_secret ? (
                                <Badge variant="outline" className="badge-neutral gap-1">
                                  <Fingerprint className="h-3 w-3" />
                                  Secret
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="badge-info gap-1">
                                  <Globe2 className="h-3 w-3" />
                                  Public
                                </Badge>
                              )}
                            </td>
                            <td className="p-3 align-top text-center">
                              <div className="flex justify-center">
                                <StatusIcon match={entry.match} />
                              </div>
                            </td>
                            <td className="p-3 align-top max-w-xs">
                              <ValueCell entry={entry} side="runtime" />
                            </td>
                            <td className="p-3 align-top max-w-xs">
                              <ValueCell entry={entry} side="dotenv" />
                            </td>
                            <td className="p-3 align-top">
                              <NoteCell entry={entry} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Aide de lecture */}
            <Card className="border-slate-800 bg-slate-900/30">
              <CardContent
                className="p-4 text-xs text-slate-400"
                data-testid="admin-env-check-help"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
                  <div className="flex items-start gap-2">
                    <Fingerprint className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                    <span>
                      <strong className="text-slate-200">Secret</strong> — affiché
                      uniquement sous forme <code>…XXXX</code> +{" "}
                      <code>sha:xxxxxxxx</code>. Comparez ces empreintes avec le
                      Dashboard Emergent (Environment → Secrets).
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Globe2 className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                    <span>
                      <strong className="text-slate-200">Public</strong> — URL, IDs
                      OAuth, config non-sensible : affichée en clair pour comparaison
                      directe.
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
